import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { apiRequest, ApiError } from "@/lib/api-client";
import { useAuthStore } from "@/lib/auth-store";
import { notificationKeys } from "@/features/notification/api";
import type {
  Appointment,
  AppointmentListQuery,
  AppointmentProposal,
  AppointmentProposalWire,
  AppointmentStatus,
  AppointmentUpdateRequest,
  AppointmentWire,
  RequestViewingInput,
  RespondInput,
} from "@/features/appointment/types";

// ---------------------------------------------------------------------------
// Wire → view-model converter
//
// See the rationale block in `features/property/api.ts`. Codegen marks
// every scalar optional; the view-model promises `T | null` for nullable
// fields and `T` for required ones. This converter normalises `undefined
// → null` for the nullables so the contract holds at runtime.
// ---------------------------------------------------------------------------

export function toAppointment(wire: AppointmentWire): Appointment {
  return {
    id: wire.id!,
    leadId: wire.leadId ?? null,
    propertyId: wire.propertyId ?? null,
    brokerId: wire.brokerId!,
    scheduledAt: wire.scheduledAt ?? "",
    status: wire.status!,
    awaitingParty: wire.awaitingParty ?? null,
    requestedByUserId: wire.requestedByUserId ?? null,
    notes: wire.notes ?? null,
    outcomeNote: wire.outcomeNote ?? null,
    // Defaults to true so any surface that doesn't yet read the flag behaves as
    // before; the backend always sends it explicitly.
    viewableInFull: wire.viewableInFull ?? true,
    proofNote: wire.proofNote ?? null,
    proofVideoUrl: wire.proofVideoUrl ?? null,
    createdAt: wire.createdAt!,
    updatedAt: wire.updatedAt!,
  };
}

export function toAppointmentProposal(
  wire: AppointmentProposalWire,
): AppointmentProposal {
  return {
    id: wire.id!,
    appointmentId: wire.appointmentId!,
    proposedByUserId: wire.proposedByUserId ?? null,
    party: wire.party!,
    action: wire.action!,
    proposedAt: wire.proposedAt ?? null,
    note: wire.note ?? null,
    createdAt: wire.createdAt!,
  };
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const appointmentKeys = {
  all: ["appointments"] as const,
  lists: () => [...appointmentKeys.all, "list"] as const,
  list: (query: AppointmentListQuery) =>
    [...appointmentKeys.lists(), query] as const,
  details: () => [...appointmentKeys.all, "detail"] as const,
  detail: (id: number) => [...appointmentKeys.details(), id] as const,
  proposals: (id: number) =>
    [...appointmentKeys.detail(id), "proposals"] as const,
  inbox: (awaiting: "me" | "them") =>
    [...appointmentKeys.all, "inbox", awaiting] as const,
};

// ---------------------------------------------------------------------------
// Network calls
// ---------------------------------------------------------------------------

function buildListPath(query: AppointmentListQuery): string {
  const params = new URLSearchParams();
  if (query.brokerId !== undefined) {
    params.set("brokerId", String(query.brokerId));
  }
  if (query.status !== undefined) {
    params.set("status", query.status);
  }
  if (query.from !== undefined) params.set("from", query.from);
  if (query.to !== undefined) params.set("to", query.to);
  if (query.leadId !== undefined) params.set("leadId", String(query.leadId));
  if (query.customerId !== undefined) {
    params.set("customerId", String(query.customerId));
  }
  const qs = params.toString();
  return qs ? `/appointments?${qs}` : "/appointments";
}

async function fetchAppointments(query: AppointmentListQuery): Promise<Appointment[]> {
  const wires = await apiRequest<AppointmentWire[]>(buildListPath(query));
  return wires.map(toAppointment);
}

async function fetchAppointment(id: number): Promise<Appointment> {
  return toAppointment(await apiRequest<AppointmentWire>(`/appointments/${id}`));
}

async function patchAppointment(
  id: number,
  body: AppointmentUpdateRequest,
): Promise<Appointment> {
  return toAppointment(
    await apiRequest<AppointmentWire>(`/appointments/${id}`, {
      method: "PATCH",
      body,
    }),
  );
}

async function patchAppointmentStatus(
  id: number,
  status: AppointmentStatus,
): Promise<Appointment> {
  return toAppointment(
    await apiRequest<AppointmentWire>(`/appointments/${id}/status`, {
      method: "PATCH",
      body: { status },
    }),
  );
}

function deleteAppointmentApi(id: number) {
  return apiRequest<void>(`/appointments/${id}`, { method: "DELETE" });
}

async function postRequestViewing(input: RequestViewingInput): Promise<Appointment> {
  return toAppointment(
    await apiRequest<AppointmentWire>("/appointments/request", {
      method: "POST",
      body: input,
    }),
  );
}

async function postRespond(id: number, input: RespondInput): Promise<Appointment> {
  return toAppointment(
    await apiRequest<AppointmentWire>(`/appointments/${id}/respond`, {
      method: "POST",
      body: input,
    }),
  );
}

async function fetchProposals(id: number): Promise<AppointmentProposal[]> {
  const wires = await apiRequest<AppointmentProposalWire[]>(
    `/appointments/${id}/proposals`,
  );
  return wires.map(toAppointmentProposal);
}

async function fetchInbox(awaiting: "me" | "them"): Promise<Appointment[]> {
  const wires = await apiRequest<AppointmentWire[]>(
    `/appointments/inbox?awaiting=${awaiting}`,
  );
  return wires.map(toAppointment);
}

// ---------------------------------------------------------------------------
// Cache helper
// ---------------------------------------------------------------------------

function syncAppointmentIntoCache(
  queryClient: QueryClient,
  appointment: Appointment,
): void {
  queryClient.setQueryData(appointmentKeys.detail(appointment.id), appointment);
  void queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useAppointments(query: AppointmentListQuery = {}) {
  return useQuery({
    queryKey: appointmentKeys.list(query),
    queryFn: () => fetchAppointments(query),
    placeholderData: (prev) => prev,
  });
}

export function useAppointment(id: number | undefined) {
  return useQuery({
    queryKey:
      id !== undefined
        ? appointmentKeys.detail(id)
        : appointmentKeys.details(),
    queryFn: () => {
      if (id === undefined) throw new Error("useAppointment called without id");
      return fetchAppointment(id);
    },
    enabled: id !== undefined,
  });
}

export function useUpdateAppointment(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AppointmentUpdateRequest) => patchAppointment(id, body),
    onSuccess: (appointment) => {
      syncAppointmentIntoCache(queryClient, appointment);
    },
  });
}

// ---------------------------------------------------------------------------
// Proof of attendance
// ---------------------------------------------------------------------------

async function uploadProofVideo(
  id: number,
  file: File,
  proofNote?: string,
): Promise<Appointment> {
  const form = new FormData();
  form.append("file", file);
  if (proofNote !== undefined && proofNote.trim() !== "") {
    form.append("proofNote", proofNote);
  }
  // Multipart must NOT go through apiRequest (it JSON-stringifies the body).
  // Raw fetch with the in-memory bearer token; let the browser set the
  // multipart boundary Content-Type itself.
  const token = useAuthStore.getState().accessToken;
  const headers = new Headers();
  if (token !== null) headers.set("Authorization", `Bearer ${token}`);
  const res = await fetch(`/api/v1/appointments/${id}/proof-video`, {
    method: "POST",
    credentials: "include",
    headers,
    body: form,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, body, body?.message ?? `Upload failed (${res.status})`);
  }
  return toAppointment((await res.json()) as AppointmentWire);
}

async function patchProofNote(id: number, proofNote: string): Promise<Appointment> {
  return toAppointment(
    await apiRequest<AppointmentWire>(`/appointments/${id}/proof-note`, {
      method: "PATCH",
      body: { proofNote },
    }),
  );
}

async function removeProofVideo(id: number): Promise<Appointment> {
  return toAppointment(
    await apiRequest<AppointmentWire>(`/appointments/${id}/proof-video`, {
      method: "DELETE",
    }),
  );
}

/** Upload / replace an appointment's proof-of-attendance clip (+ optional caption). */
export function useUploadProofVideo(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { file: File; proofNote?: string }) =>
      uploadProofVideo(id, vars.file, vars.proofNote),
    onSuccess: (appointment) => syncAppointmentIntoCache(queryClient, appointment),
  });
}

/** Edit the proof-of-attendance caption without re-uploading the clip. */
export function useUpdateProofNote(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (proofNote: string) => patchProofNote(id, proofNote),
    onSuccess: (appointment) => syncAppointmentIntoCache(queryClient, appointment),
  });
}

/** Remove the proof-of-attendance clip (the caption is kept). */
export function useRemoveProofVideo(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => removeProofVideo(id),
    onSuccess: (appointment) => syncAppointmentIntoCache(queryClient, appointment),
  });
}

interface StatusContext {
  previousLists: Array<[readonly unknown[], Appointment[] | undefined]>;
  previousDetail: Appointment | undefined;
}

/**
 * Optimistic status transition. Updates every cached list + the detail
 * cache immediately; rolls back on error and surfaces the localized
 * message via the caller's `onError`.
 */
export function useChangeAppointmentStatus(id: number) {
  const queryClient = useQueryClient();
  return useMutation<Appointment, Error, AppointmentStatus, StatusContext>({
    mutationFn: (status: AppointmentStatus) => patchAppointmentStatus(id, status),
    onMutate: async (status) => {
      await queryClient.cancelQueries({ queryKey: appointmentKeys.lists() });
      await queryClient.cancelQueries({ queryKey: appointmentKeys.detail(id) });

      const previousLists = queryClient.getQueriesData<Appointment[]>({
        queryKey: appointmentKeys.lists(),
      });
      for (const [key, data] of previousLists) {
        if (!data) continue;
        queryClient.setQueryData<Appointment[]>(
          key,
          data.map((a) => (a.id === id ? { ...a, status } : a)),
        );
      }

      const previousDetail = queryClient.getQueryData<Appointment>(
        appointmentKeys.detail(id),
      );
      if (previousDetail !== undefined) {
        queryClient.setQueryData<Appointment>(appointmentKeys.detail(id), {
          ...previousDetail,
          status,
        });
      }

      return { previousLists, previousDetail };
    },
    onError: (_err, _vars, context) => {
      if (!context) return;
      for (const [key, data] of context.previousLists) {
        queryClient.setQueryData(key, data);
      }
      if (context.previousDetail !== undefined) {
        queryClient.setQueryData(
          appointmentKeys.detail(id),
          context.previousDetail,
        );
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: appointmentKeys.detail(id),
      });
    },
  });
}

interface DeleteContext {
  previousLists: Array<[readonly unknown[], Appointment[] | undefined]>;
}

export function useDeleteAppointment() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, number, DeleteContext>({
    mutationFn: (id: number) => deleteAppointmentApi(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: appointmentKeys.lists() });
      const previousLists = queryClient.getQueriesData<Appointment[]>({
        queryKey: appointmentKeys.lists(),
      });
      for (const [key, data] of previousLists) {
        if (!data) continue;
        queryClient.setQueryData<Appointment[]>(
          key,
          data.filter((a) => a.id !== id),
        );
      }
      return { previousLists };
    },
    onError: (_err, _id, context) => {
      if (!context) return;
      for (const [key, data] of context.previousLists) {
        queryClient.setQueryData(key, data);
      }
    },
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: appointmentKeys.detail(id) });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
    },
  });
}

/**
 * Invalidate every cache a request/respond can affect: the appointment
 * itself + lists, its proposal timeline, the notification badge (a "your
 * turn" notification was just emitted), and the lead queries (the lead's
 * appointment list + VIEWING_SCHEDULED gate eligibility may have changed).
 */
function invalidateAfterNegotiation(
  queryClient: QueryClient,
  appointment: Appointment,
): void {
  syncAppointmentIntoCache(queryClient, appointment);
  void queryClient.invalidateQueries({
    queryKey: appointmentKeys.proposals(appointment.id),
  });
  void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
  void queryClient.invalidateQueries({ queryKey: ["leads"] });
  void queryClient.invalidateQueries({
    queryKey: [...appointmentKeys.all, "inbox"],
  });
}

/**
 * After a FAILED negotiation move — most importantly a 409 because the other
 * party already acted — refetch everything that may now be stale so the panel
 * and inbox reflect the real server state instead of leaving dead buttons.
 */
function refetchAfterConflict(queryClient: QueryClient, id: number): void {
  void queryClient.invalidateQueries({ queryKey: appointmentKeys.detail(id) });
  void queryClient.invalidateQueries({
    queryKey: appointmentKeys.proposals(id),
  });
  void queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
  void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
  void queryClient.invalidateQueries({ queryKey: ["leads"] });
  void queryClient.invalidateQueries({
    queryKey: [...appointmentKeys.all, "inbox"],
  });
}

/** An Agent requests a viewing — opens the verification negotiation. */
export function useRequestViewing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RequestViewingInput) => postRequestViewing(input),
    onSuccess: (appointment) =>
      invalidateAfterNegotiation(queryClient, appointment),
  });
}

/** Respond in a negotiation (ACCEPT / COUNTER / REJECT). */
export function useRespondToAppointment(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RespondInput) => postRespond(id, input),
    onSuccess: (appointment) =>
      invalidateAfterNegotiation(queryClient, appointment),
    onError: () => refetchAfterConflict(queryClient, id),
  });
}

/**
 * Respond from a list context where the appointment id isn't fixed at hook
 * call time (the inbox rows). Same invalidation as the id-bound variant.
 */
export function useRespondToViewing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: RespondInput }) =>
      postRespond(id, input),
    onSuccess: (appointment) =>
      invalidateAfterNegotiation(queryClient, appointment),
    onError: (_error, variables) =>
      refetchAfterConflict(queryClient, variables.id),
  });
}

/**
 * The current user's verification inbox — pending viewings awaiting them
 * (`awaiting="me"`) or awaiting the other party (`awaiting="them"`).
 */
export function useViewingInbox(awaiting: "me" | "them") {
  return useQuery({
    queryKey: appointmentKeys.inbox(awaiting),
    queryFn: () => fetchInbox(awaiting),
    placeholderData: (prev) => prev,
  });
}

/** The negotiation timeline for one appointment (oldest first). */
export function useAppointmentProposals(id: number | undefined, enabled = true) {
  return useQuery({
    queryKey:
      id !== undefined ? appointmentKeys.proposals(id) : appointmentKeys.all,
    queryFn: () => {
      if (id === undefined) {
        throw new Error("useAppointmentProposals called without id");
      }
      return fetchProposals(id);
    },
    enabled: id !== undefined && enabled,
  });
}
